import { Add } from '@mui/icons-material'
import { Box, Button, Menu, MenuItem, Stack, Typography, useMediaQuery } from '@mui/material'
import gaiaTheme from '../../../../root-components/theme';
import { useContext, useEffect, useState } from 'react';
import DistributionListService from '../../../../shared/services/distribution-list/distribution-list.service';
import { IDistributionList } from '../../../../shared/services/distribution-list/distribution-list.model';
import { IHeadCell } from '../../../../shared/interfaces/table-display.model';
import moment from 'moment';
import { TableDisplay } from '../../../presentation-components/miscellaneous-components/table-display';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AddDistributionListManuallyDialog } from '../../../dialogs/settings/distribution-list/add-distribution-list-manually-dialog';
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications';
import { FileUploaderDialog } from '../../../dialogs/file-uploader-dialog';
import { SettingsMobileNavbar } from '../../../../root-components/navigation/settings-mobile-navbar';
import { ReportPeriodContext } from '../../../../context/report-periods/report-periods';
import { LoadingPage } from '../../loading-page';
import { PageOuterContainer } from '../../../../root-components/layouts/page-outer-container';
import { useHasOpenReportPeriod } from '../../../../hooks/use-has-open-report-period';
import { deleteQueryParamFromUrl } from '../../../../shared/utils/misc-shared-utils';

export const DistributionListsPage = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  //Services
  const distributionListService = new DistributionListService()
  const reportPeriodContext = useContext(ReportPeriodContext)
  const userNotificationsContext = useContext(UserNotificationsContext)
  //State
  const [openManualDialog, setOpenManualDialog] = useState<boolean>(false)
  const [openFileDialog, setOpenFileDialog] = useState<boolean>(false)
  const [distributionLists, setDistributionLists] = useState<IDistributionList[]>()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const openDropdown = Boolean(anchorEl)
  //Table State
  const [selected, setSelected] = useState<string[]>([]);
  const [inputPage, setInputPage] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0)
  const [perPage, setPerPage] = useState(10)
  const [change, setChange] = useState<number>(0)
  const [orderBy, setOrderBy] = useState<string>()
  const [order, setOrder] = useState<'asc' | 'desc'>()
  //Loading State
  const [hasPageMounted, setHasPageMounted] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  //Layout
  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('md'));
  //Hooks 
  const hasOpenReport = useHasOpenReportPeriod([
    reportPeriodContext.allReportPeriods,
    reportPeriodContext.lastSelectedOpenReportId,
    openFileDialog,
  ]);
  //Use Effects
  useEffect(() => {
    if (searchParams.has('openDialog')) {
      if (searchParams.get('openDialog') === 'true') {
        setOpenManualDialog(true);
        deleteQueryParamFromUrl(searchParams, 'openDialog')
      }
      deleteQueryParamFromUrl(searchParams, 'openDialog')
    }
  }, []);

  useEffect(() => {
    getDistributionLists();
  }, [inputPage, perPage, change])

  useEffect(() => {
    if (hasPageMounted) {
      if (inputPage === 0) {
        setChange(change + 1)
      } else {
        setInputPage(0)
      }
    }
    setHasPageMounted(true)
  }, [order, orderBy])

  const getDistributionLists = (callbackFunction?: () => void) => {
    distributionListService.getAllDistributionListsWithPagination(inputPage, perPage, orderBy, order)
      .then(response => {
        setIsLoading(false)
        setTotalRecords(response.count);
        setDistributionLists(response.rows)
        if (callbackFunction) {
          callbackFunction()
        }
      })
      .catch(err => {
        setIsLoading(false)
        console.log(err)
      })
  }

  //Table Contents
  const headCells: IHeadCell[] = [
    { id: 'listName', numeric: false, label: 'Name', sortable: true, isHeading: true },
    { id: 'createdAt', numeric: false, label: 'Created on', sortable: true, includeInMobile: false },
    { id: 'updatedAt', numeric: false, label: 'Last Edited', sortable: false, includeInMobile: false },
  ];

  const rows = !distributionLists ? [] : distributionLists.map((list) => {
    const listUpdatedAt = moment(list.updatedAt);
    const maxAddressUpdatedAt = list.distributionListAddresses.length > 0
      ? moment.max(list.distributionListAddresses.map(address => moment(address.updatedAt)))
      : listUpdatedAt;
    const mostRecentUpdatedAt = moment.max(listUpdatedAt, maxAddressUpdatedAt);
    return {
      id: list.id,
      listName: list.listName,
      createdAt: moment(list.createdAt).format('DD/MM/YYYY'),
      updatedAt: mostRecentUpdatedAt.format('DD/MM/YYYY'),
    }
  })

  //Table Event Handlers
  const handleOrderChange = (orderBy: string, order: 'asc' | 'desc',) => {
    setOrderBy(orderBy)
    setOrder(order)
  };

  const handleSelected = (selectedRowId: string[]) => {
    if (selectedRowId[0] !== undefined) {
      navigate(`/settings/company-settings/distribution-lists/${selectedRowId[0]}`)
    }
  }

  const openManualDialogHandler = () => {
    setOpenManualDialog(true)
    setAnchorEl(null)
  }

  const openFileDialogHandler = () => {
    setOpenFileDialog(true)
    setAnchorEl(null)
  }

  const dialogManualSuccessHandler = () => {
    // getDistributionLists(() => setOpenManualDialog(false))
    getDistributionLists()
    setOpenManualDialog(false)
  }

  const closeManualDialogHandler = () => {
    setOpenManualDialog(false)
  }

  const dialogFileSuccessHandler = () => {
    userNotificationsContext.openSnackbar({ open: true, message: 'Imported successfully' });
    getDistributionLists()
    setOpenFileDialog(false)
  }

  const dialogFileFailureHandler = (message?: string) => {
    setOpenFileDialog(false)
    userNotificationsContext.openDialog({
      active: true, title: 'Action Required',
      message: message,
      buttonCopy: 'Ok',
      hideCancelButton: true
    })
  }

  return (
    <PageOuterContainer>
      {isMobileMode && <Box sx={{ pb: 4 }}>
        <SettingsMobileNavbar />
      </Box>}
      <Stack direction={isMobileMode ? 'column' : 'row'} justifyContent='space-between' alignItems={isMobileMode ? 'flex-start' : 'center'} sx={{ mb: 4 }}>
        <Typography variant='h4' sx={{ color: 'text.primary', pb: 1 }}>Distribution Lists</Typography>
        <Stack display='flex' direction={isMobileMode ? 'column' : 'row'} spacing={1}>
          <Button
            id="basic-button"
            variant='text'
            aria-controls={openDropdown ? 'basic-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={openDropdown ? 'true' : undefined}
            onClick={(e) => setAnchorEl(e.currentTarget)}>
            <Add />
            <Typography>Add Distribution List</Typography>
          </Button>
          <Menu
            id="basic-menu"
            anchorEl={anchorEl}
            open={openDropdown}
            onClose={() => setAnchorEl(null)}
            MenuListProps={{
              'aria-labelledby': 'basic-button',
            }}
          >
            <MenuItem onClick={() => openManualDialogHandler()}>Create Manually</MenuItem>
            {hasOpenReport && <MenuItem onClick={() => openFileDialogHandler()}>Import via CSV/Excel</MenuItem>}
          </Menu>
        </Stack>
      </Stack>
      <LoadingPage pageReady={!isLoading}>
        <TableDisplay
          onOrderChange={handleOrderChange}
          isMobileMode={isMobileMode}
          headCells={headCells}
          rows={rows}
          haveCheckboxSelect={false}
          handleSelected={handleSelected}
          selected={selected}
          setSelected={setSelected}
          inputPage={inputPage}
          setInputPage={setInputPage}
          totalRecords={totalRecords}
          perPage={perPage}
          setPerPage={setPerPage} />
      </LoadingPage>
      {openManualDialog &&
        <AddDistributionListManuallyDialog
          open={openManualDialog}
          closeHandler={closeManualDialogHandler}
          successHandler={dialogManualSuccessHandler}
        />}
      {openFileDialog &&
        <FileUploaderDialog
          typeOfData='distribution-list'
          dialogActive={openFileDialog}
          onSuccess={dialogFileSuccessHandler}
          cancelUpload={() => { setOpenFileDialog(false) }}
          onFailure={dialogFileFailureHandler} />}
    </PageOuterContainer>
  )
}